import axios from 'axios';
import { store } from '../store';
import { logout } from '../features/account/accountSlice';

/**
 * Code the backend returns alongside a 401 when the rejection is caused by the
 * account's own standing — locked, expired, unapproved or closed — rather than
 * by the token.
 *
 * The distinction matters: an expired token is worth logging in again, whereas
 * a revoked account fails with any token, so the only sensible response is to
 * clear the stored session rather than surface an error on every subsequent
 * call. Must match `codeAccountRevoked` in the backend's cmd/agency/session.go.
 */
export const CODE_ACCOUNT_REVOKED = 'ACCOUNT_REVOKED';

interface ErrorPayload {
    code?: string;
    error?: string;
}

export const isRevokedPayload = (payload: unknown): payload is ErrorPayload =>
    typeof payload === 'object' &&
    payload !== null &&
    (payload as ErrorPayload).code === CODE_ACCOUNT_REVOKED;

// Requests fail in parallel, so without this every in-flight call would raise
// its own alert and dispatch its own navigation.
let ending = false;

/**
 * Clears the stored session and returns to login. Safe to call from anywhere,
 * including outside React.
 */
export const endRevokedSession = (message?: string) => {
    if (ending) return;
    ending = true;
    console.warn('session revoked:', message);
    window.alert(`账户已停用，请重新登录。${message ? `\n(${message})` : ''}`);
    store.dispatch(logout());
};

/**
 * Ends the session if the payload carries the revoked code. Returns whether it
 * did, so a caller can skip its own error handling.
 */
export const handleRevokedPayload = (payload: unknown): boolean => {
    if (!isRevokedPayload(payload)) return false;
    endRevokedSession(payload.error);
    return true;
};

// Guards against a second call within one module lifetime, which would stack
// fetch wrappers and register a duplicate interceptor. Harmless if it ever slips
// through — the `ending` latch collapses the duplicate handling — but cheap.
let guardInstalled = false;

/**
 * Routes both HTTP transports through `handleRevokedPayload`.
 *
 * The portal calls `axios` and `fetch` directly rather than through a shared
 * client, so patching the two globals is the only place every request passes
 * through. Doing it here rather than at each call site also covers hooks
 * written later, which an opt-in helper would not.
 *
 * When the TypeScript migration lands a typed API wrapper (as `src/lib/api.ts`
 * did in the admin portal), that wrapper subsumes the fetch patch.
 */
export const installSessionGuard = () => {
    if (guardInstalled) return;
    guardInstalled = true;

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            handleRevokedPayload(error?.response?.data);
            return Promise.reject(error);
        }
    );

    // Bound up front: `fetch` throws "Illegal invocation" if it loses `window`
    // as its receiver, and this captures the unpatched implementation.
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
        const response = await originalFetch(...args);
        // Cloning buffers the body, so only pay for it on the one status that
        // can carry the revoked code. The caller still reads the original.
        if (response.status === 401) {
            try {
                handleRevokedPayload(await response.clone().json());
            } catch {
                // A 401 with a non-JSON body is not a revocation. Third-party
                // requests (Sentry ingest and friends) land here too.
            }
        }
        return response;
    };
};
